import React, { useState } from 'react'
import { Box, Typography, Chip, Tooltip } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import { useTranslation } from 'react-i18next'
import { useSelector, useDispatch } from 'react-redux'
import ReactGA from 'react-ga4'
import { tracker } from '../../../analytics'
import { PlayCircle } from 'lucide-react'
import SearchHeader from './SearchHeader'
import TutorialModal from '../../../components/TutorialModal'
import {
  setSearchQuery,
  setSearchDocument,
  setSearchDocumentSourceUrl,
  setSearchSelectedCitation,
  filteredSearchCitations,
  clearPreSearchFilters,
} from '../../../redux/actions/searchAction'
import { useSearchTokens } from '../searchDesignTokens'

const EXAMPLE_QUERY_KEYS_BY_COUNTRY = {
  MA: [
    'eq_dismissal_compensation',
    'eq_tort_liability',
    'eq_contract_nullity',
    'eq_unfair_competition',
    'eq_police_custody',
  ],
  AE: [
    'eq_employment_termination',
    'eq_commercial_agency',
    'eq_civil_liability',
    'eq_contract_nullification',
    'eq_labour_dispute',
  ],
}

const DEFAULT_QUERY_KEYS = [
  'eq_civil_liability',
  'eq_dismissal_compensation',
  'eq_contract_nullity',
  'eq_unfair_competition',
  'eq_appeal_procedure',
]

const SearchLanding = ({ mode, setMode }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const dk = useSearchTokens()
  const [demoOpen, setDemoOpen] = useState(false)

  const countryPref = useSelector(state => state.user?.countryPreference)
  // Use first selected country for example queries and display label
  const countryPreference = Array.isArray(countryPref) ? (countryPref[0] || 'MA') : (countryPref || 'MA')
  const isAbrogeFilter = useSelector(state => state.search.isAbrogeFilter)
  const isLoadingSearchCitations = useSelector(state => state.search.isLoadingSearchCitations)
  const preSearchFilters = useSelector(state => state.search.preSearchFilters) || {}
  const activePreFilterCount = Object.values(preSearchFilters).reduce((acc, modeFilters) => {
    if (!modeFilters || typeof modeFilters !== 'object') return acc
    return acc + Object.values(modeFilters).filter(v =>
      Array.isArray(v) ? v.length > 0 : v !== null && v !== undefined
    ).length
  }, 0)
  const hasActivePreFilters = activePreFilterCount > 0

  const exampleQueryKeys = EXAMPLE_QUERY_KEYS_BY_COUNTRY[countryPreference] || DEFAULT_QUERY_KEYS

  const getCountryLabel = () => {
    return t(`search_country_${countryPreference}`, countryPreference)
  }

  const handleExampleQuery = async (queryKey) => {
    if (isLoadingSearchCitations) return
    const query = t(queryKey)
    dispatch(setSearchDocument(null))
    dispatch(setSearchDocumentSourceUrl(null))
    dispatch(setSearchSelectedCitation(null))
    dispatch(setSearchQuery(query))
    const filters = isAbrogeFilter ? { is_active: true } : null
    await dispatch(filteredSearchCitations(query, mode, filters))
    ReactGA.event({
      category: 'Search',
      action: 'User used example query',
      label: query,
    })
    tracker.event({
      category: 'Search',
      action: 'User used example query',
      label: query,
    })
  }

  return (
    <Box
      data-tour="search-landing"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        px: { xs: 2, md: 6 },
        py: 4,
        backgroundColor: dk.surface,
        position: 'relative',
        /* Subtle radial glow behind the heading */
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '18%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '70%',
          height: '40%',
          background: `radial-gradient(ellipse 70% 50% at 50% 30%, ${dk.goldGlow} 0%, transparent 70%)`,
          pointerEvents: 'none',
        },
      }}
    >
      {/* Heading */}
      <Box sx={{ textAlign: 'center', mb: 4, position: 'relative', zIndex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mb: 1.5 }}>
          <SearchIcon sx={{ fontSize: '2rem', color: dk.gold }} />
          <Typography
            variant="h4"
            sx={{
              fontWeight: 300,
              color: dk.text1,
              letterSpacing: '-0.02em',
              fontFamily: '"Cormorant Garamond", Georgia, "Times New Roman", serif',
              fontSize: { xs: '1.8rem', md: '2.2rem' },
            }}
          >
            {t('legal_search_engine')}
          </Typography>
        </Box>
        <Typography
          variant="body1"
          sx={{ color: dk.text2, maxWidth: 500, mx: 'auto', fontSize: '0.95rem', lineHeight: 1.6 }}
        >
          {t('search_landing_subtitle', { country: getCountryLabel() })}
        </Typography>
      </Box>

      {/* Search bar */}
      <Box sx={{ width: '100%', maxWidth: 700, position: 'relative', zIndex: 1 }}>
        <SearchHeader mode={mode} setMode={setMode} />
      </Box>

      {/* Active pre-filter indicator */}
      {hasActivePreFilters && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            mt: 1,
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Chip
            label={`${activePreFilterCount} ${activePreFilterCount === 1 ? t('filter_applied') : t('filters_applied')}`}
            size="small"
            onDelete={() => dispatch(clearPreSearchFilters())}
            sx={{
              backgroundColor: dk.goldSub,
              color: dk.gold,
              border: `1px solid ${dk.goldBdr}`,
              fontSize: '0.7rem',
              fontWeight: 500,
              height: 24,
              '& .MuiChip-deleteIcon': {
                color: dk.gold,
                fontSize: '0.85rem',
                '&:hover': { color: dk.text1 },
              },
            }}
          />
        </Box>
      )}

      {/* Example query chips */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1.5,
          mt: 3,
          width: '100%',
          maxWidth: 700,
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <AutoFixHighIcon sx={{ fontSize: 13, color: dk.disabled }} />
          <Typography
            variant="caption"
            sx={{
              color: dk.disabled,
              fontWeight: 600,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              fontSize: '0.62rem',
            }}
          >
            {t('example_queries')}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            gap: 1,
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}
        >
          {exampleQueryKeys.map((queryKey) => (
            <Chip
              key={queryKey}
              label={t(queryKey)}
              clickable
              disabled={isLoadingSearchCitations}
              onClick={() => handleExampleQuery(queryKey)}
              sx={{
                backgroundColor: dk.goldSub,
                color: dk.gold,
                border: `1px solid ${dk.goldBdr}`,
                borderRadius: '20px',
                fontSize: '0.78rem',
                fontWeight: 500,
                height: 32,
                transition: 'all 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
                '& .MuiChip-label': { px: 1.5 },
                '&:hover': {
                  backgroundColor: dk.goldHover,
                  borderColor: dk.gold,
                  transform: 'translateY(-1px)',
                  boxShadow: `0 4px 16px ${dk.goldGlow}`,
                },
                '&:active': { transform: 'translateY(0)' },
                '&.Mui-disabled': { opacity: 0.35 },
              }}
            />
          ))}
        </Box>
      </Box>

      {/* Watch Demo button */}
      <Tooltip title={t('watch_demo_tooltip')} arrow>
        <Box
          component="button"
          onClick={() => setDemoOpen(true)}
          sx={{
            mt: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            px: 2,
            py: 0.9,
            border: `1px solid ${dk.border}`,
            borderRadius: '20px',
            backgroundColor: 'transparent',
            cursor: 'pointer',
            transition: 'all 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
            position: 'relative',
            zIndex: 1,
            '&:hover': {
              borderColor: dk.goldBdr,
              backgroundColor: dk.goldSub,
              transform: 'translateY(-1px)',
            },
            '&:active': { transform: 'translateY(0)' },
          }}
        >
          <PlayCircle size={14} color={dk.text2} />
          <Typography
            sx={{
              fontSize: '0.75rem',
              color: dk.text2,
              fontWeight: 500,
              letterSpacing: '0.04em',
              userSelect: 'none',
            }}
          >
            {t('watch_demo')}
          </Typography>
        </Box>
      </Tooltip>

      <TutorialModal open={demoOpen} onClose={() => setDemoOpen(false)} defaultTab="search" />
    </Box>
  )
}

export default SearchLanding
